import type { Components } from '@/api/generated/api';
import type { IDropdownSubmenuActionGroup, IDropdownSubmenuActionOption, ISelectOption } from '@/interfaces';
import { SCHOOL_SUBJECT_NAME_MAX_LENGTH } from 'common/constants/school.subject.constants';
import { EIcon } from 'common/enum/misc.enum';
import { immutableSort } from 'common/utils/array.utils';
import { getOrderedSchoolSubjects } from 'common/utils/school.subject.utils';

type TSchoolSubjectOrder = Pick<Components.Schemas.SchoolSubjectDto, 'groupId' | 'order'>;
type TSchoolSubjectGroupOrder = Pick<Components.Schemas.SchoolSubjectGroupDto, 'id' | 'order'>;

export interface ISchoolSubjectMoveTarget {
  targetGroupId: number | null;
  targetSchoolLevel?: number;
}

export interface ISchoolSubjectMovePayload extends ISchoolSubjectMoveTarget {
  schoolSubject: Components.Schemas.SchoolSubjectDto;
}

export const getSchoolSubjectGroupsWithSubjects = <TGroup extends TSchoolSubjectGroupOrder>(
  schoolSubjectGroups: TGroup[],
  schoolSubjects: Pick<Components.Schemas.SchoolSubjectDto, 'groupId'>[],
): TGroup[] => {
  const groupIdsWithSubjects = new Set(
    schoolSubjects
      .map((schoolSubject) => schoolSubject.groupId)
      .filter((groupId): groupId is number => groupId != null),
  );

  return immutableSort(
    schoolSubjectGroups.filter((schoolSubjectGroup) => groupIdsWithSubjects.has(schoolSubjectGroup.id)),
    (a, b) => a.order - b.order,
  );
};

/**
 * Whether a pill row would offer a real choice. A single subject is selected implicitly by
 * SchoolSubjectFilter, so its pill is decoration — and the row can be left out. Counted on the
 * ordered subjects, since those are the ones the row actually renders.
 */
export const hasSchoolSubjectPillChoice = (
  schoolSubjects: TSchoolSubjectOrder[],
  availableSchoolSubjectGroups: TSchoolSubjectGroupOrder[],
): boolean => getOrderedSchoolSubjects(schoolSubjects, availableSchoolSubjectGroups).length > 1;

export const getIsDomainMatchingSubject = (
  competenceId: number,
  availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[],
  selectedSchoolSubjectId: number | null,
): boolean => {
  const domainFound = availableCompetenceDomains.find((domain) =>
    domain.competences.some((c) => c.id === competenceId),
  );

  if (!domainFound) return false;
  if (!selectedSchoolSubjectId) return true;

  return !!(domainFound.schoolSubjectId && selectedSchoolSubjectId === domainFound.schoolSubjectId);
};

export const getAccessibleSchoolSubjectId = (
  schoolSubjectId: number | null | undefined,
  accessibleSubjectIds: Set<number>,
): number | null => {
  if (schoolSubjectId == null) return null;
  return accessibleSubjectIds.has(schoolSubjectId) ? schoolSubjectId : null;
};

export const resolveSchoolSubjectMoveTarget = (value: number | string): ISchoolSubjectMoveTarget => {
  if (typeof value === 'number') {
    return { targetGroupId: value };
  }
  const [schoolLevel, groupId] = value.split(':');
  return { targetSchoolLevel: Number(schoolLevel), targetGroupId: groupId === 'ungrouped' ? null : Number(groupId) };
};

export const getDuplicateSchoolSubjectName = (name: string, suffix: string): string =>
  `${name.slice(0, SCHOOL_SUBJECT_NAME_MAX_LENGTH - suffix.length).trimEnd()}${suffix}`;

export const resolveSchoolSubjectMoveGroups = ({
  subjects,
  levelGroups,
  groupOptions = [],
}: {
  subjects: Components.Schemas.SchoolSubjectDto[];
  levelGroups?: IDropdownSubmenuActionGroup<string>[];
  groupOptions?: IDropdownSubmenuActionOption<number>[];
}): IDropdownSubmenuActionGroup[] => {
  if (levelGroups?.length) {
    return disableCurrentSchoolSubjectDestination(levelGroups, subjects);
  }

  // Flat class/provider menus omit the common current group; level menus keep it visible but disabled.
  const options = groupOptions.filter(
    (option) => !subjects.length || !subjects.every((subject) => subject.groupId === option.value),
  );
  return [{ options }];
};

export const buildSchoolSubjectMoveGroups = (params: {
  levels: ISelectOption<number>[];
  groups: Components.Schemas.SchoolSubjectGroupDto[];
  activeLevel: number;
  ungroupedLabel: string;
}): IDropdownSubmenuActionGroup<string>[] => {
  return params.levels.map((level) => ({
    id: level.value,
    label: level.label,
    collapsible: true,
    initiallyExpanded: level.value === params.activeLevel,
    options: [
      { label: params.ungroupedLabel, value: `${level.value}:ungrouped`, iconBefore: EIcon.FOLDER },
      ...immutableSort(
        params.groups.filter((group) => group.schoolLevel === level.value),
        (a, b) => a.order - b.order,
      ).map((group) => ({
        label: group.name,
        value: `${level.value}:${group.id}`,
        iconBefore: EIcon.FOLDER,
      })),
    ],
  }));
};

export const disableCurrentSchoolSubjectDestination = (
  groups: IDropdownSubmenuActionGroup<string>[],
  subjects: Components.Schemas.SchoolSubjectDto[],
): IDropdownSubmenuActionGroup<string>[] => {
  return groups.map((group) => ({
    ...group,
    options: group.options.map((option) => {
      const target = resolveSchoolSubjectMoveTarget(option.value);
      const isCurrentDestination =
        subjects.length > 0 &&
        subjects.every(
          (subject) => subject.schoolLevel === target.targetSchoolLevel && subject.groupId === target.targetGroupId,
        );
      return { ...option, disabled: option.disabled || isCurrentDestination };
    }),
  }));
};
